// Turnstile.tsx
import { TURNSTILE_SITE_ID } from '@/config';
import { useEffect, useRef } from 'react';

type TurnstileTheme = 'light' | 'dark' | 'auto';
type TurnstileSize = 'normal' | 'compact';

interface TurnstileOptions {
  'onToken': (token: string) => void;
  'siteKey'?: string;
  'action'?: string;
  'cdata'?: string;
  'theme'?: TurnstileTheme;
  'size'?: TurnstileSize;
  'callback'?: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
  'before-interactive-callback'?: () => void;
  'after-interactive-callback'?: () => void;
  'unsupported-callback'?: () => void;
  'language'?: string;
  'tabindex'?: number;
  'response-field'?: boolean;
  'response-field-name'?: string;
  'retry'?: 'auto' | 'never';
  'retry-interval'?: number;
  'refresh-expired'?: 'auto' | 'manual' | 'never';
  'appearance'?: 'always' | 'execute' | 'interaction-only';
}

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, options: Record<string, any>) => string;
      reset: (id?: string) => void;
    };
    __turnstileScriptPromise?: Promise<void>;
  }
}

export function Turnstile({ onToken, siteKey, action, cdata }: TurnstileOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    if (window.turnstile) return;

    if (!window.__turnstileScriptPromise) {
      window.__turnstileScriptPromise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = false;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Turnstile script'));
        document.head.appendChild(script);
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!window.turnstile && window.__turnstileScriptPromise) {
        try {
          await window.__turnstileScriptPromise;
        } catch (e) {
          console.error('[Turnstile] script failed to load', e);
          return;
        }
      }

      if (cancelled) return;
      if (!window.turnstile) return;
      if (!containerRef.current) return;

      const resolvedSiteKey = siteKey || (TURNSTILE_SITE_ID as string | undefined);

      if (!resolvedSiteKey) {
        console.error('[Turnstile] Missing env');
        return;
      }

      const id = window.turnstile.render(containerRef.current, {
        'sitekey': resolvedSiteKey,
        'callback': (token: string) => onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current(''),
        'timeout-callback': () => onTokenRef.current(''),
        'error-callback': () => onTokenRef.current(''),
        'theme': 'light',
        'appearance': 'execute',
        'size': 'normal',
        'language': 'en',
        'refresh': 'auto',
        ...(action ? { action } : {}),
        ...(cdata ? { cdata } : {}),
      });

      widgetIdRef.current = id;
    }

    init();

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {
          // ignore
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, action, cdata]);

  return <div ref={containerRef} />;
}

export default Turnstile;
